// components/LeadForm.tsx — BBK in-page lead capture (replaces external bartoszgaca.pl/fit-call CTAs).
// Phase 1 scaffold: UI works, POSTs to /api/leads, returns whatsapp_handoff_url.
// NOT mounted into pages yet — Phase 1.1 will replace fit-call links with <LeadFormModal/>.

import React, { useRef, useState } from 'react';
import { read as readAttribution } from '../utils/utm';
import { trackPlausibleEvent } from '../utils/tracking';

/**
 * Pytanie kwalifikujące. Renderowane ZAWSZE przed polami kontaktowymi — po wysłaniu kontaktu
 * użytkownik już nie wraca i pytania nie zobaczy. Ta sama zasada, którą wymusza nasz kod
 * formularzy błyskawicznych Meta (fb-ads/src/lib/meta/write.ts:120: "Pytanie kwalifikujące MUSI
 * iść przed polami kontaktowymi").
 */
export interface LeadQualifier {
    label: string;
    options: string[];
}

interface LeadFormProps {
    productSlug?: string;
    source?: string;
    qualifier?: LeadQualifier;
    onSubmitted?: (leadId: number, waHandoffUrl: string) => void;
}

interface SubmitResult {
    ok: boolean;
    lead_id: number | null;
    whatsapp_handoff_url?: string;
}

export const LeadForm: React.FC<LeadFormProps> = ({ productSlug, source, qualifier, onSubmitted }) => {
    const [qualifierAnswer, setQualifierAnswer] = useState('');
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [message, setMessage] = useState('');
    const [website, setWebsite] = useState(''); // honeypot — humans leave empty
    const [submitting, setSubmitting] = useState(false);
    const [result, setResult] = useState<SubmitResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const formStartTime = useRef<number | null>(null);

    function handleFocusIn() {
        if (!formStartTime.current) formStartTime.current = Date.now();
    }

    async function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        setError(null);

        try {
            const res = await fetch('/api/leads', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    source: source || 'site_form',
                    product_slug: productSlug,
                    name, email, phone, website,
                    // Odpowiedź kwalifikującą doklejamy do treści, żeby trafiła do panelu /admin/leady
                    // nawet zanim backend dostanie osobne pole. Bez tego wybór ginie po drodze.
                    message: qualifier && qualifierAnswer
                        ? `[${qualifier.label}: ${qualifierAnswer}]${message ? `\n${message}` : ''}`
                        : message,
                    qualifier: qualifier ? { label: qualifier.label, answer: qualifierAnswer } : undefined,
                    attribution: readAttribution(),
                }),
            });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const data = (await res.json()) as SubmitResult;
            setResult(data);
            if (data.lead_id) {
                trackPlausibleEvent('lead_submit', { product: productSlug || 'unknown' });
                // GTM lead event — picked up by GA4 generate_lead + Meta Lead trigger
                const elapsed = formStartTime.current
                    ? Math.round((Date.now() - formStartTime.current) / 1000)
                    : 0;
                (window as any).dataLayer = (window as any).dataLayer || [];
                (window as any).dataLayer.push({
                    event: 'contact_form_submit',
                    form_completion_time: elapsed,
                    form_source: source || 'site_form',
                    product_slug: productSlug || 'unknown',
                    // event_id matches server-side CAPI fire for browser↔server dedup
                    meta_event_id: `bbk-lead-${data.lead_id}`,
                    meta_event_name: 'Lead',
                });
                formStartTime.current = null;
                onSubmitted?.(data.lead_id, data.whatsapp_handoff_url || '');
            }
        } catch (err) {
            console.error('[LeadForm] submit failed', err);
            setError('Coś poszło nie tak. Spróbuj jeszcze raz lub napisz na WhatsApp.');
        } finally {
            setSubmitting(false);
        }
    }

    if (result?.ok && result.lead_id) {
        return (
            <div className="rounded-lg p-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
                <h3 className="text-lg font-bold mb-2">Dzięki — mamy Twoje zgłoszenie ✅</h3>
                <p className="text-sm mb-4">Wysłaliśmy potwierdzenie na e-mail. Wolisz od razu rozmowę?</p>
                {result.whatsapp_handoff_url && (
                    <a
                        href={result.whatsapp_handoff_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-[#25D366] text-white py-2 px-4 rounded-lg font-semibold hover:opacity-90"
                    >
                        Otwórz WhatsApp →
                    </a>
                )}
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} onFocus={handleFocusIn} className="space-y-3">
            {qualifier && (
                <fieldset className="space-y-2">
                    <legend className="text-base font-bold mb-2">{qualifier.label}</legend>
                    {qualifier.options.map(opt => (
                        <label
                            key={opt}
                            className={`flex items-center gap-3 w-full px-4 py-3 border rounded-lg cursor-pointer transition ${
                                qualifierAnswer === opt
                                    ? 'border-primary-light dark:border-primary-dark bg-primary-light/10 dark:bg-primary-dark/10 font-semibold'
                                    : 'bg-white dark:bg-gray-800 hover:border-gray-400'
                            }`}
                        >
                            <input
                                type="radio" name="qualifier" value={opt}
                                checked={qualifierAnswer === opt}
                                onChange={e => setQualifierAnswer(e.target.value)}
                                className="accent-current"
                            />
                            <span>{opt}</span>
                        </label>
                    ))}
                </fieldset>
            )}
            <input
                type="text" name="name" placeholder="Imię (opcjonalne)" value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800"
            />
            <input
                type="email" name="email" placeholder="E-mail" value={email}
                onChange={e => setEmail(e.target.value)} required
                className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800"
            />
            <input
                type="tel" name="phone" placeholder="Telefon (opcjonalny)" value={phone}
                onChange={e => setPhone(e.target.value)}
                className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800"
            />
            <textarea
                name="message" placeholder="Twoja sytuacja w 1-2 zdaniach (opcjonalne)"
                value={message} onChange={e => setMessage(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-800"
            />

            {/* Honeypot — hidden from users, bots fill it */}
            <input
                type="text" name="website" value={website}
                onChange={e => setWebsite(e.target.value)}
                tabIndex={-1} autoComplete="off"
                style={{ position: 'absolute', left: '-9999px' }}
                aria-hidden="true"
            />

            {error && <p className="text-sm text-red-600">{error}</p>}

            <button
                type="submit" disabled={submitting}
                data-analytics-id="leadform_submit"
                className="w-full bg-primary-light dark:bg-primary-dark text-white py-2 px-4 rounded-md font-semibold disabled:opacity-50"
            >
                {submitting ? 'Wysyłam…' : 'Wyślij zgłoszenie'}
            </button>
        </form>
    );
};
